import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { billableWords, smallestPlanFor, SUPPORTED_LOCALE_COUNT } from '../../utils/wordBudget';
import { CalculatorMessages as M, CalculatorText as T } from './WordBudgetCalculator.messages';
import './word-budget-calculator.css';

/**
 * Turns the pricing model into arithmetic the visitor can check.
 *
 * The plans are quoted in words, and usage is billed as source words × target
 * locales — so the number of languages, not the size of the app, dominates the
 * bill. That was disclosed only in /docs, while /pricing listed "5,000 words" and
 * "56 locales" side by side as though they were independent. They are not: a
 * modest app of 1,000 source words into all 56 locales is 56,000 billable words,
 * past Starter's entire annual allowance.
 *
 * Developers discovered this at the worst possible moment — a first sync that
 * failed with a bare HTTP 403 — and concluded the product was broken rather than
 * that they needed a bigger plan. Showing the multiplication before signup costs
 * nothing and replaces that discovery with a decision.
 *
 * Defaults are deliberate: 5,200 words is this site's own interface, measured, and
 * 5 languages is a realistic first target. That lands on Starter at $108 — the
 * honest answer for a real app, and a far better anchor than the figure the same
 * app reaches across all 56 locales, which is 291,200 words and needs Scale.
 */
const DEFAULT_SOURCE_WORDS = 5_200;
const DEFAULT_LOCALES = 5;

/** Keeps a typed value sane without fighting the person typing it. */
function clampNumber(raw: string, max: number): number {
  const parsed = Number.parseInt(raw.replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.min(parsed, max);
}

export const WordBudgetCalculator: React.FC = () => {
  const { t } = useTranslation();
  const [sourceWords, setSourceWords] = useState(DEFAULT_SOURCE_WORDS);
  const [locales, setLocales] = useState(DEFAULT_LOCALES);

  const total = billableWords(sourceWords, locales);
  const plan = smallestPlanFor(sourceWords, locales);

  return (
    <section className="wbc" aria-labelledby="wbc-heading">
      <div className="container">
        <h2 id="wbc-heading">{t(M.HEADING, { defaultValue: T.HEADING })}</h2>
        <p className="wbc-lede">{t(M.LEDE, { defaultValue: T.LEDE })}</p>

        <div className="wbc-inputs">
          <div className="wbc-field">
            <label htmlFor="wbc-words">{t(M.LABEL_SOURCE_WORDS, { defaultValue: T.LABEL_SOURCE_WORDS })}</label>
            <input
              id="wbc-words"
              /*
               * type="text" with inputMode numeric, not type="number": a number input
               * gives spinners nobody uses at this scale and silently accepts values
               * like "1e9". The parser below is the validation.
               */
              type="text"
              inputMode="numeric"
              value={sourceWords === 0 ? '' : sourceWords.toLocaleString()}
              onChange={e => setSourceWords(clampNumber(e.target.value, 10_000_000))}
              aria-describedby="wbc-words-hint"
            />
            <p className="wbc-hint" id="wbc-words-hint">
              {t(M.HINT_SOURCE_WORDS, { defaultValue: T.HINT_SOURCE_WORDS })}
            </p>
          </div>

          <div className="wbc-field">
            <label htmlFor="wbc-locales">{t(M.LABEL_LOCALES, { defaultValue: T.LABEL_LOCALES })}</label>
            <input
              id="wbc-locales"
              type="range"
              min={1}
              max={SUPPORTED_LOCALE_COUNT}
              value={locales}
              onChange={e => setLocales(Number(e.target.value))}
              aria-valuetext={String(locales)}
            />
            <output className="wbc-locale-count" htmlFor="wbc-locales">
              {locales} / {SUPPORTED_LOCALE_COUNT}
            </output>
          </div>
        </div>

        {/*
          A live region: the numbers change as the slider moves, and a screen-reader
          user needs the result announced rather than silently rewritten.
        */}
        <div className="wbc-result" role="status" aria-live="polite">
          <p className="wbc-total">
            {t(M.RESULT_TOTAL, {
              defaultValue: T.RESULT_TOTAL,
              sourceWords: sourceWords.toLocaleString(),
              locales,
              total: total.toLocaleString(),
            })}
          </p>
          <p className="wbc-plan">
            {plan === null
              ? (
                <>
                  {t(M.RESULT_OVER_LARGEST, { defaultValue: T.RESULT_OVER_LARGEST })}{' '}
                  <Link to="/contact">{t(M.CONTACT_LINK, { defaultValue: T.CONTACT_LINK })}</Link>
                </>
              )
              : plan.annualPriceUsd === 0
                ? t(M.RESULT_PLAN_FREE, { defaultValue: T.RESULT_PLAN_FREE })
                : t(M.RESULT_PLAN, { defaultValue: T.RESULT_PLAN, plan: plan.name })}
          </p>
        </div>

        <p className="wbc-note">{t(M.FIRST_SYNC_NOTE, { defaultValue: T.FIRST_SYNC_NOTE })}</p>
      </div>
    </section>
  );
};
